/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * HelpPanel - Slide-out Process Guides Panel
 * ============================================================================
 *
 * A slide-out panel from the right side that shows the guide browser and
 * guide detail view. Opened via the Help button in the Header.
 *
 * Uses CoreX theme variables (NOT UAT dark glassmorphism).
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import GuideBrowser from '@/components/guides/GuideBrowser';
import ProcessGuideView from '@/components/guides/ProcessGuideView';
import type { ProcessGuide } from '@/types/process-guides.types';

interface HelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpPanel({ isOpen, onClose }: HelpPanelProps) {
  const { t } = useTranslation('platform');
  const [selectedGuide, setSelectedGuide] = useState<ProcessGuide | null>(null);

  const handleClose = () => {
    setSelectedGuide(null);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`help-panel-overlay ${isOpen ? 'help-panel-overlay--visible' : ''}`}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={`help-panel ${isOpen ? 'help-panel--open' : ''}`}
        role="dialog"
        aria-label={t('guides.title')}
      >
        {/* Header */}
        <div className="help-panel__header">
          <h2 className="help-panel__title">{t('guides.title')}</h2>
          <button
            className="help-panel__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="help-panel__content">
          {!selectedGuide ? (
            <GuideBrowser onSelectGuide={setSelectedGuide} />
          ) : (
            <ProcessGuideView
              guide={selectedGuide}
              onBack={() => setSelectedGuide(null)}
            />
          )}
        </div>
      </div>

      <style>{helpPanelStyles}</style>
    </>
  );
}

const helpPanelStyles = `
  /* ---- Overlay ---- */
  .help-panel-overlay {
    position: fixed;
    inset: 0;
    z-index: 999;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    pointer-events: none;
    transition: opacity 300ms ease;
  }

  .help-panel-overlay--visible {
    opacity: 1;
    pointer-events: auto;
  }

  /* ---- Panel ---- */
  .help-panel {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 480px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    background: var(--color-bg-primary);
    border-left: 1px solid var(--color-border-subtle);
    box-shadow: var(--shadow-xl);
    transform: translateX(100%);
    transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .help-panel--open {
    transform: translateX(0);
  }

  /* ---- Header ---- */
  .help-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .help-panel__title {
    font-family: var(--font-heading);
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0;
  }

  .help-panel__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .help-panel__close:hover {
    background: var(--color-bg-hover, var(--color-bg-tertiary));
    color: var(--color-text-primary);
  }

  /* ---- Content ---- */
  .help-panel__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
  }

  /* ---- Mobile ---- */
  @media (max-width: 640px) {
    .help-panel {
      width: 100%;
    }
  }

  /* ---- Reduced motion ---- */
  @media (prefers-reduced-motion: reduce) {
    .help-panel {
      transition: none !important;
    }
    .help-panel-overlay {
      transition: none !important;
    }
  }
`;
